import '../input';
import '../icon-button';
import '../icon';
import '../text';
import { attrValueToPixels, defineCustomElement, getAttribute, getBooleanAttribute, unpackCsv, getFirstCsvValue, getIntegerAttribute, getReactEventHandler, isAttrTrue, NectaryElement, updateAttribute, updateBooleanAttribute, updateCsv, updateExplicitBooleanAttribute, updateIntegerAttribute, debounceTimeout, setClass, subscribeContext, hasClass, isTargetEqual } from '../utils';
const templateHTML = '<style>:host{display:block;outline:0}#listbox{overflow-y:auto}#search{display:none;margin:10px}#search.active{display:block}#search-clear:not(.active){display:none}#not-found{display:flex;align-items:center;justify-content:center;width:100%;height:30px;margin-bottom:10px;pointer-events:none;user-select:none;--sinch-comp-text-font:var(--sinch-comp-select-menu-font-not-found-text);--sinch-global-color-text:var(--sinch-comp-select-menu-color-default-not-found-text-initial)}#not-found:not(.active){display:none}::slotted(.hidden){display:none}</style><sinch-input id="search" size="s" placeholder="Search"><sinch-icon id="icon-search" slot="icon" name="search"></sinch-icon><sinch-icon-button id="search-clear" slot="right"><sinch-icon slot="icon" name="close"></sinch-icon></sinch-icon-button></sinch-input><div id="not-found"><sinch-text type="m">No results</sinch-text></div><div id="listbox" role="presentation"><slot></slot></div>';
const ITEM_HEIGHT = 40;
const NUM_ITEMS_SEARCH = 7;
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-select-menu', class extends NectaryElement {
  #$optionSlot;
  #$listbox;
  #$search;
  #$searchClear;
  #$notFound;
  #controller = null;
  #searchDebounce;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$optionSlot = shadowRoot.querySelector('slot');
    this.#$listbox = shadowRoot.querySelector('#listbox');
    this.#$search = shadowRoot.querySelector('#search');
    this.#$searchClear = shadowRoot.querySelector('#search-clear');
    this.#$notFound = shadowRoot.querySelector('#not-found');
    this.#searchDebounce = debounceTimeout(200)(this.#updateSearch);
  }
  connectedCallback() {
    this.#controller = new AbortController();
    const options = {
      signal: this.#controller.signal
    };
    this.role = 'listbox';
    this.tabIndex = 0;
    this.addEventListener('keydown', this.#onListboxKeyDown, options);
    this.addEventListener('focus', this.#onFocus, options);
    this.addEventListener('blur', this.#onListboxBlur, options);
    this.#$listbox.addEventListener('click', this.#onListboxClick, options);
    this.#$search.addEventListener('-change', this.#onSearchChange, options);
    this.#$searchClear.addEventListener('-click', this.#onSearchClearClick, options);
    this.#$optionSlot.addEventListener('slotchange', this.#onOptionSlotChange, options);
    this.addEventListener('-search-change', this.#onSearchChangeReactHandler, options);
    this.addEventListener('-change', this.#onChangeReactHandler, options);
    subscribeContext(this, 'keydown', this.#onContextKeyDown, this.#controller.signal);
    subscribeContext(this, 'visibility', this.#onContextVisibility, this.#controller.signal);
    this.#onOptionSlotChange();
  }
  disconnectedCallback() {
    this.#searchDebounce.cancel();
    this.#controller.abort();
    this.#controller = null;
  }
  static get observedAttributes() {
    return ['value', 'rows', 'multiple', 'search-placeholder'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'multiple':
        {
          this.#onValueChange(this.value);
          updateExplicitBooleanAttribute(this, 'aria-multiselectable', isAttrTrue(newVal));
          break;
        }
      case 'value':
        {
          this.#onValueChange(newVal ?? '');
          break;
        }
      case 'rows':
        {
          this.#$listbox.style.maxHeight = attrValueToPixels(newVal, {
            min: 2,
            itemSizeMultiplier: ITEM_HEIGHT
          });
          break;
        }
      case 'search-placeholder':
        {
          updateAttribute(this.#$search, 'placeholder', newVal);
          break;
        }
    }
  }
  set value(value) {
    updateAttribute(this, 'value', value);
  }
  get value() {
    return getAttribute(this, 'value', '');
  }
  set rows(value) {
    updateIntegerAttribute(this, 'rows', value);
  }
  get rows() {
    return getIntegerAttribute(this, 'rows', null);
  }
  set multiple(isMultiple) {
    updateBooleanAttribute(this, 'multiple', isMultiple);
  }
  get multiple() {
    return getBooleanAttribute(this, 'multiple');
  }
  set searchable(isSearchable) {
    updateBooleanAttribute(this, 'searchable', isSearchable);
  }
  get searchable() {
    const searchableAttribute = this.getAttribute('searchable');
    return searchableAttribute === null ? searchableAttribute : isAttrTrue(searchableAttribute);
  }
  set 'search-placeholder'(placeholder) {
    updateAttribute(this.#$search, 'placeholder', placeholder);
  }
  get 'search-placeholder'() {
    return getAttribute(this.#$search, 'placeholder', '');
  }
  get focusable() {
    return true;
  }
  #onFocus = () => {
    const isSearchActive = hasClass(this.#$search, 'active');
    if (isSearchActive) {
      this.#$search.focus();
    }
  };
  #onListboxBlur = () => {
    this.#selectOption(null);
  };
  #onListboxClick = e => {
    const $elem = e.target;
    this.focus();
    if (!isTargetEqual(e, this.#$listbox) && !getBooleanAttribute($elem, 'disabled')) {
      this.#selectOption($elem);
      this.#dispatchChangeEvent($elem);
    }
  };
  #onSearchChange = e => {
    this.#$search.value = e.detail;
    this.#searchDebounce.fn();
    setClass(this.#$searchClear, 'active', e.detail.length > 0);
  };
  #onSearchClearClick = () => {
    this.#$search.value = '';
    this.#$search.focus();
    this.#searchDebounce.fn();
    setClass(this.#$searchClear, 'active', false);
  };
  #updateSearch = () => {
    const searchValue = this.#$search.value.toLowerCase();
    const searchChangedEvent = new CustomEvent('-search-change', {
      detail: searchValue,
      cancelable: true
    });
    this.dispatchEvent(searchChangedEvent);
    if (!searchChangedEvent.defaultPrevented) {
      const $options = this.#getOptionElements();
      let someFound = false;
      for (const $opt of $options) {
        const isHidden = searchValue.length > 0 && !$opt.matchesSearch(searchValue);
        someFound ||= !isHidden;
        setClass($opt, 'hidden', isHidden);
      }
      setClass(this.#$notFound, 'active', !someFound);
      this.#selectOption(null);
    }
  };
  #onContextKeyDown = e => {
    this.#handleKeydown(e.detail);
  };
  #onContextVisibility = e => {
    if (e.detail) {
      this.#selectOption(this.#findCheckedOption());
      this.#onFocus();
    } else {
      this.#selectOption(null);
    }
  };
  #onListboxKeyDown = e => {
    this.#handleKeydown(e);
  };
  #handleKeydown(e) {
    switch (e.code) {
      case 'Space':
      case 'Enter':
        {
          e.preventDefault();
          const $option = this.#findSelectedOption();
          if ($option !== null) {
            this.#dispatchChangeEvent($option);
          }
          break;
        }
      case 'ArrowUp':
        {
          e.preventDefault();
          this.#selectOption(this.#getPrevOption());
          break;
        }
      case 'ArrowDown':
        {
          e.preventDefault();
          this.#selectOption(this.#getNextOption());
          break;
        }
    }
  }
  #onOptionSlotChange = () => {
    const searchable = this.searchable;
    const options = this.#$optionSlot.assignedElements();
    const isEnoughOptions = options.length >= NUM_ITEMS_SEARCH;
    const isSearchActive = isEnoughOptions && searchable !== false || Boolean(searchable);
    if (!isSearchActive) {
      updateAttribute(this.#$search, 'value', null);
    }
    setClass(this.#$search, 'active', isSearchActive);
    this.#onValueChange(this.value);
  };
  #onValueChange(csv) {
    if (this.multiple) {
      const values = unpackCsv(csv);
      for (const $option of this.#getOptionElements()) {
        const isChecked = !getBooleanAttribute($option, 'disabled') && values.includes(getAttribute($option, 'value', ''));
        updateBooleanAttribute($option, 'data-checked', isChecked);
      }
    } else {
      const value = getFirstCsvValue(csv);
      for (const $option of this.#getOptionElements()) {
        const isChecked = !getBooleanAttribute($option, 'disabled') && value === getAttribute($option, 'value', '');
        updateBooleanAttribute($option, 'data-checked', isChecked);
      }
    }
  }
  #getFirstOption() {
    const $options = this.#getOptionElements();
    for (let i = 0; i < $options.length; i++) {
      const el = $options[i];
      if (!getBooleanAttribute(el, 'disabled') && !el.classList.contains('hidden')) {
        return el;
      }
    }
    return null;
  }
  #getLastOption() {
    const $options = this.#getOptionElements();
    for (let i = $options.length - 1; i >= 0; i--) {
      const el = $options[i];
      if (!getBooleanAttribute(el, 'disabled') && !el.classList.contains('hidden')) {
        return el;
      }
    }
    return null;
  }
  #getNextOption() {
    const index = this.#getSelectedOptionIndex();
    if (index !== null) {
      const $options = this.#getOptionElements();
      for (let i = 1; i <= $options.length; i++) {
        const el = $options[(i + index) % $options.length];
        if (!getBooleanAttribute(el, 'disabled') && !el.classList.contains('hidden')) {
          return el;
        }
      }
    }
    return this.#getFirstOption();
  }
  #getPrevOption() {
    const index = this.#getSelectedOptionIndex();
    if (index !== null) {
      const $options = this.#getOptionElements();
      for (let i = 1; i <= $options.length; i++) {
        const el = $options[(index - i + $options.length) % $options.length];
        if (!getBooleanAttribute(el, 'disabled') && !el.classList.contains('hidden')) {
          return el;
        }
      }
    }
    return this.#getLastOption();
  }
  #selectOption($option) {
    const hasRows = this.hasAttribute('rows');
    for (const $op of this.#getOptionElements()) {
      const isSelected = $op === $option;
      updateBooleanAttribute($op, 'data-selected', isSelected);
      if (isSelected && hasRows) {
        $op.scrollIntoView?.({
          block: 'nearest'
        });
      }
    }
  }
  #getOptionElements() {
    return this.#$optionSlot.assignedElements();
  }
  #getSelectedOptionIndex() {
    const elements = this.#getOptionElements();
    for (let i = 0; i < elements.length; i++) {
      const el = elements[i];
      if (!getBooleanAttribute(el, 'disabled') && getBooleanAttribute(el, 'data-selected')) {
        return i;
      }
    }
    return null;
  }
  #findSelectedOption() {
    const elements = this.#getOptionElements();
    for (const el of elements) {
      if (!getBooleanAttribute(el, 'disabled') && getBooleanAttribute(el, 'data-selected')) {
        return el;
      }
    }
    return null;
  }
  #findCheckedOption() {
    const elements = this.#getOptionElements();
    const value = this.multiple ? getFirstCsvValue(this.value) : this.value;
    for (const $el of elements) {
      if (!getBooleanAttribute($el, 'disabled') && getAttribute($el, 'value') === value) {
        return $el;
      }
    }
    return null;
  }
  #dispatchChangeEvent($opt) {
    if ($opt === null) {
      return;
    }
    const value = $opt.value;
    const result = this.multiple ? updateCsv(this.value, value, !getBooleanAttribute($opt, 'data-checked')) : value;
    this.dispatchEvent(new CustomEvent('-change', {
      detail: result
    }));
  }
  #onChangeReactHandler = e => {
    getReactEventHandler(this, 'on-change')?.(e);
  };
  #onSearchChangeReactHandler = e => {
    getReactEventHandler(this, 'on-search-change')?.(e);
  };
});